import * as React from 'react';
import { ChangeSexParams, changeSex, getIndividuals, getIndividualsColumns, restoreIndividualsColumnsToDefault as restoreIndividualsColumnsToDefaultBase, saveIndividualsColumns } from '../services/individuals';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { IndividualListDto } from '@common/dto/individual/list-dto';
import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import { getModuleFolders } from '@common/actions/folders';
import { I18n } from 'react-redux-i18n';
import { getFolderFields } from '@common/actions/formats';
import { FieldListDto } from '@common/dto/field/list-dto';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { generateId } from '@common/utils/common';
import { getFamilyIdMembersCount, renameIndividual } from '@common/actions/individuals';
import { getQuestionnaires, launchNewIndividualQuestionnaire } from '@common/actions/questionnaires';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { HttpErrorDto } from '@common/dto/http';

export type IndividualsControllerProps = {
  showErrorMessage: (msg: string) => void;
  showInfoMessage: (msg: string) => void;
  pedigreesSelectedItems: Array<PedigreeListDto>;
  getColumnId: (col: ColumnListDto) => string;
};

export const useIndividualsController = (props: IndividualsControllerProps) => {
  const { getColumnId, pedigreesSelectedItems, showErrorMessage, showInfoMessage } = props;
  const individualItemUniqProperty = 'customIndividualItemUniqProperty';

  const [individualsLoadMetadata, setIndividualsLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: {
      size: 50,
      start: 0,
    },
  });
  const [individualsCount, setIndividualsCount] = React.useState(0);
  const [individuals, setIndividuals] = React.useState<Array<IndividualListDto>>([]);
  const [individualsLoading, setIndividualsLoading] = React.useState(false);
  const [individualsColumns, setIndividualsColumns] = React.useState<Array<ColumnListDto>>([]);
  const [individualsSelectedItems, setIndividualsSelectedItems] = React.useState<Array<IndividualListDto>>([]);

  const handleGetIndividuals = async (params: NewTableLoadMetadata & { 
    columns: Array<ColumnListDto>; 
    pedigreeId: PedigreeListDto['id']; 
    loadedIndividuals: Array<IndividualListDto> 
  }) => {
    try {
      setIndividualsLoading(true);

      const { filtering, pagination, sorting } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });
  
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = params.columns.findIndex((x) => getColumnId(x) == columnId);
        const columnToRemoveBecauseOfActionsColumn = 1;
        const value = filters[columnId];
  
        return {
          columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
          operator1: value?.operator1 ?? null,
          operator2: value?.operator2 ?? null,
          value1: value?.whereClause1 ?? null,
          value2: value?.whereClause2 ?? null,
          andOr: value?.andOR,
        };
      });

      const { data, totalCount } = await getIndividuals({
        pagination,
        filtering: {
          filterApplied: filtering?.filter ?? false,
          columnFilters: columnFiltersProcessed,
          columns,
          pedigreeId: params.pedigreeId,
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[individualItemUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.loadedIndividuals, ...processedData]
        : processedData;

      setIndividuals(finalData);
      setIndividualsCount((x) => pagination.start > 0 ? x : totalCount);
      setIndividualsLoadMetadata({
        pagination,
        sorting,
        filtering
      });
    } finally {
      setIndividualsLoading(false);
    }
  };

  const updateIndividualsColumns = React.useCallback(async (columns: Array<ColumnListDto>) => {
    await saveIndividualsColumns(columns);
    setIndividualsColumns(columns);
  }, []);

  const getAndSetIndividualsColumns = React.useCallback(async () => {
    const cols = await getIndividualsColumns();
    setIndividualsColumns(cols);

    return cols;
  }, []);

  const resetIndividualsList = React.useCallback(() => {
    setIndividualsLoadMetadata((x) => ({
      pagination: {
        start: 0,
        size: x.pagination.size,
      },
      sorting: undefined,
      filtering: undefined,
    }));
    setIndividualsCount(0);
    setIndividuals([]);
    setIndividualsSelectedItems([]);
  }, []);

  const handleIndividualSelectedItemsChange = React.useCallback((individuals: IndividualListDto[]) => {
    setIndividualsSelectedItems(individuals);
  }, []); 

  const getFoldersForIndividualAndInviteColumn = React.useCallback(() => {
    const individualsId = 1;

    return getModuleFolders({ folderTypeId: individualsId, setUp: { rootFolderImgUrl: 'gif/df_individual.gif', rootFolderName: I18n.t('Individual Data Folders') } });
  }, []);

  const getModuleFolderFieldsForIndividualAndInviteColumn = React.useCallback((params: { searchQuery?: string; folderId?: FolderPrimaryListDto['id']; system: boolean }) => {
    const individualsContext = 1;
    
    return getFolderFields({
      searchQuery: params.searchQuery,
      folderId: params.folderId,
      contextId: individualsContext,
      includeTables: false,
    }).then((fields) => fields.map((field) => ({
      fieldId: field.id,
      fieldName: field.name,
      fieldContext: field.context,
      fieldTypeId: field.type,
      systemField: field.system,
      imageURL: field.imageURL,
      fullPath: field.fullPath,
      options: field.options,
    })) as unknown as FieldListDto[]);
  }, []);

  const restoreIndividualsColumnsToDefault = React.useCallback(async () => {
    const cols = await restoreIndividualsColumnsToDefaultBase();
    setIndividualsColumns(cols);
    return cols;
  }, []);

  const getQuestionnairesForNewQuestionnaire = React.useCallback(async (familyId: IndividualListDto['familyId']) => {
    const membersCount = await getFamilyIdMembersCount(familyId);
    const isSinglet = individuals.find((x) => x.familyId === familyId)?.singlet === 'true';
    const questionnaires = await getQuestionnaires({
      includeFamilyQuestionnaires: !isSinglet && membersCount <= 1,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });

    return questionnaires;
  }, [individuals]);

  const handleLaunchNewQuestionnaire = React.useCallback(async (globalIdId: IndividualListDto['id'], questionnaire: QuestionnaireListDto | undefined) => {
    const redirectUrl = await launchNewIndividualQuestionnaire(
      globalIdId, 
      questionnaire?.questionnaireId,
      questionnaire?.fhq4
    );
    window.location.href = redirectUrl;
  }, []);

  const onRefreshIndividualsOrChangeIndividualsColumns = () => {
    setIndividualsSelectedItems([]);
  };

  const handleIndividualsColumnsOrderChange = async (columns: Array<ColumnListDto>) => {
    const currentSortingColumnExists = columns.some((x) => getColumnId(x) === individualsLoadMetadata?.sorting?.id);
    const newIndividualsLoadMetadata: NewTableLoadMetadata = {
      sorting: currentSortingColumnExists 
        ? individualsLoadMetadata?.sorting!
        : undefined,
      pagination: {
        start: 0,
        size: 45,
      },
    };

    await updateIndividualsColumns(columns);
    await getAndSetIndividualsColumns();

    if (pedigreesSelectedItems.length) {
      handleGetIndividuals({
        columns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedIndividuals: [],
        ...newIndividualsLoadMetadata
      }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get individuals');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshIndividualsOrChangeIndividualsColumns();
    }
  };

  const handleChangeIndividualSex = async (
    data: ChangeSexParams
  ) => {
    await changeSex(data);
    await handleGetIndividuals({
      filtering: individualsLoadMetadata.filtering,
      sorting: individualsLoadMetadata.sorting,
      pagination: {
        size: individualsLoadMetadata.pagination.size,
        start: 0,
      },
      columns: individualsColumns,
      pedigreeId: pedigreesSelectedItems[0]?.id!,
      loadedIndividuals: [],
    }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage =
        (err as HttpErrorDto)?.entity ||
        I18n.t("Failed to get individuals");
      showErrorMessage(errorMessage);
    });
  };

  const handleIndividualRename = async (id: IndividualListDto['id'], name: string) => {
    await renameIndividual(id, name);
    showInfoMessage(I18n.t('Individual successfully renamed'));

    if (pedigreesSelectedItems.length) {
      handleGetIndividuals({
        filtering: individualsLoadMetadata.filtering,
        sorting: individualsLoadMetadata.sorting,
        pagination: {
          size: individualsLoadMetadata.pagination.size,
          start: 0,
        },
        columns: individualsColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedIndividuals: [],
      }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get individuals');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshIndividualsOrChangeIndividualsColumns();
    }
  };

  const handleRestoreIndividualsColumnsToDefault = async () => {
    const columns = await restoreIndividualsColumnsToDefault();

    if (pedigreesSelectedItems.length) {
      handleGetIndividuals({
        sorting: undefined,
        filtering: undefined,
        pagination: {
          size: individualsLoadMetadata.pagination.size,
          start: 0,
        },
        columns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedIndividuals: [],
      }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get individuals');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshIndividualsOrChangeIndividualsColumns();
    }
  };

  return {
    handleRestoreIndividualsColumnsToDefault,
    handleIndividualRename,
    onRefreshIndividualsOrChangeIndividualsColumns,
    handleIndividualsColumnsOrderChange,
    getQuestionnairesForNewQuestionnaire,
    handleLaunchNewQuestionnaire,
    individualItemUniqProperty,
    getModuleFolderFieldsForIndividualAndInviteColumn,
    getFoldersForIndividualAndInviteColumn,
    resetIndividualsList,
    individualsLoadMetadata,
    individuals,
    individualsLoading,
    individualsCount,
    handleGetIndividuals,
    individualsSelectedItems,
    individualsColumns,
    getAndSetIndividualsColumns,
    updateIndividualsColumns,
    handleIndividualSelectedItemsChange,
    handleChangeIndividualSex
  };
};